import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Link,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

import { AuthService } from '../../services/auth.service';
import { Dispatch, RootState } from '../../store';
import { SystemUserTypes } from '../../types';
import {
    CONSENT_TITLE,
    CONSENT_INTRO,
    CONSENT_POINTS,
    CONSENT_FULL_TERMS_URL,
    CONSENT_FULL_TERMS_LINK_LABEL,
    CONSENT_CONFIRM_LINE,
} from './terms-content';

// Bump this whenever the terms text changes: every user whose accepted
// version no longer matches is re-prompted at next login. Keep it date-prefixed
// (lexicographic order = chronological — the API downgrade guard relies on it)
// and add the new id to the API's PUBLISHED_TERMS_VERSIONS in the same release.
// This is the short trial consent; the full v0.2 terms remain in terms-content
// as the operative terms of record, referenced from the consent copy.
export const TERMS_VERSION = '2026-09-04-ira-trial-consent-v1';

/**
 * Whether the signed-in user must accept the terms before using the app.
 * Only members of a standalone trial org, on any login until they accept the
 * current version (so, in practice, their first login). POM staff never see
 * it — neither masquerading (acceptance is a legal act of the customer, and
 * the API rejects masquerade writes) nor direct POM Master logins, which
 * belong to no customer org at all.
 */
export const needsTermsAcceptance = (
    authUser: Partial<SystemUserTypes.Model> | null | undefined,
    isIraStandalone: boolean,
): boolean => {
    if (!isIraStandalone) return false;
    if (!authUser?.id || !authUser?.subscriptionId) return false;
    if (authUser.pomMaster || authUser.masquarade) return false;
    return authUser.termsAcceptedVersion !== TERMS_VERSION;
};

export const TermsAcceptanceGate: React.FC = () => {
    const { authUser } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<Dispatch>();
    const [accepting, setAccepting] = useState(false);

    const handleAccept = async () => {
        setAccepting(true);
        try {
            const result = await AuthService.acceptTerms(TERMS_VERSION);
            // Store the version WE sent, not the server echo: needsTermsAcceptance
            // compares against TERMS_VERSION, so a surprise value would leave the
            // gate up with no way past it. The server validates the id against its
            // own allowlist, so a mismatch means the accept did not really take —
            // surface it rather than clearing the gate.
            const savedVersion = result?.termsAcceptedVersion;
            if (savedVersion && savedVersion !== TERMS_VERSION) {
                throw new Error(`server recorded ${savedVersion}, expected ${TERMS_VERSION}`);
            }
            setAccepting(false);
            dispatch.auth.updateUser({
                ...authUser,
                termsAcceptedAt: result?.termsAcceptedAt ?? new Date().toISOString(),
                termsAcceptedVersion: TERMS_VERSION,
            });
        } catch {
            toast('Could not record your acceptance. Please try again.', { type: 'error' });
            setAccepting(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 600, fontSize: 20, pb: 0.25 }}>
                {CONSENT_TITLE}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mb: 2 }}>
                    {CONSENT_INTRO}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {CONSENT_POINTS.map((point) => (
                        <Box component="li" key={point.lead} sx={{ mb: 1.75 }}>
                            <Typography sx={{ fontSize: 13.5 }}>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {point.lead}
                                </Box>{' '}
                                <Box component="span" sx={{ color: 'text.secondary' }}>
                                    {point.body}
                                </Box>
                            </Typography>
                        </Box>
                    ))}
                </Box>
                <Typography sx={{ fontSize: 12.5, mt: 1, mb: 2.5 }}>
                    <Link href={CONSENT_FULL_TERMS_URL} target="_blank" rel="noopener noreferrer" underline="always">
                        {CONSENT_FULL_TERMS_LINK_LABEL}
                    </Link>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {CONSENT_CONFIRM_LINE}
                    </Typography>
                    <LoadingButton variant="contained" loading={accepting} onClick={handleAccept}>
                        I Agree
                    </LoadingButton>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
